'use client';

// ═══════════════════════════════════════════════════════════════
// useSettings Hook
// Access theme settings from context
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useThemeContext } from '../data-provider';

interface ColorSettings {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    text?: string;
}

interface HeaderSettings {
    transparent?: boolean;
    showSearch?: boolean;
    showCart?: boolean;
    showAnnouncement?: boolean;
    announcementText?: string;
    stickyAnnouncement?: boolean;
    stickyNavbar?: boolean;
    links?: Array<{ label: string; href: string }>;
}

interface FooterSettings {
    copyright?: string;
    showSocial?: boolean;
    showNewsletter?: boolean;
    links?: Array<{ label: string; href: string }>;
}

interface HomeSettings {
    heroImage?: string;
    heroTitle?: string;
    heroSubtitle?: string;
    heroButtonText?: string;
    heroButtonLink?: string;
    carouselTitle?: string;
}

interface SettingsShape {
    colors?: ColorSettings;
    header?: HeaderSettings;
    footer?: FooterSettings;
    home?: HomeSettings;
    [key: string]: unknown;
}

interface UseSettingsReturn {
    /** All settings */
    settings: SettingsShape;

    /** Color settings */
    colors: ColorSettings;

    /** Header settings */
    header: HeaderSettings;

    /** Footer settings */
    footer: FooterSettings;

    /** Home page settings */
    home: HomeSettings;

    /** Get a specific setting by path (e.g., 'colors.primary') */
    getSetting: <T>(path: string, defaultValue?: T) => T;
}

/**
 * Hook to access theme settings
 * Provides typed access to common settings sections
 */
export function useSettings(): UseSettingsReturn {
    const { settings } = useThemeContext();

    const typedSettings = (settings || {}) as SettingsShape;

    // Memoize derived objects to prevent infinite loops in consumers relying on referential equality
    const colors = useMemo(() => typedSettings.colors || {}, [typedSettings.colors]);
    const header = useMemo(() => typedSettings.header || {}, [typedSettings.header]);
    const footer = useMemo(() => typedSettings.footer || {}, [typedSettings.footer]);
    const home = useMemo(() => typedSettings.home || {}, [typedSettings.home]);

    const getSetting = useMemo(() => {
        return <T,>(path: string, defaultValue?: T): T => {
            const keys = path.split('.');
            let value: unknown = typedSettings;

            for (const key of keys) {
                if (value && typeof value === 'object' && key in value) {
                    value = (value as Record<string, unknown>)[key];
                } else {
                    return defaultValue as T;
                }
            }

            return (value ?? defaultValue) as T;
        };
    }, [typedSettings]);

    return {
        settings: typedSettings,
        colors,
        header,
        footer,
        home,
        getSetting,
    };
}

export default useSettings;
